import { useCallback, useEffect, useMemo, useState } from "react";
import { Menu, Trophy, RefreshCw, Users } from "lucide-react";
import { toast } from "react-toastify";
import UserSidebar from "../UserSidebar";
import {
  getLeaderboardByExamType,
  getLeaderboardExamTypes,
} from "../../services/TestService";

const REFRESH_INTERVAL_MS = 30000;

const LeaderboardPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingExamTypes, setLoadingExamTypes] = useState(false);
  const [examTypeOptions, setExamTypeOptions] = useState([]);
  const [selectedExamType, setSelectedExamType] = useState("");
  const [leaderboardRows, setLeaderboardRows] = useState([]);
  const [totalParticipants, setTotalParticipants] = useState(0);

  const fetchExamTypes = useCallback(async () => {
    try {
      setLoadingExamTypes(true);
      const response = await getLeaderboardExamTypes();
      setExamTypeOptions(response?.data || []);
    } catch (error) {
      const message = error?.response?.data?.message || "Không thể tải danh sách loại đề";
      toast.error(message);
    } finally {
      setLoadingExamTypes(false);
    }
  }, []);

  const fetchLeaderboard = useCallback(async () => {
    if (!selectedExamType) {
      setLeaderboardRows([]);
      setTotalParticipants(0);
      return;
    }

    try {
      setLoading(true);
      const response = await getLeaderboardByExamType({
        examType: selectedExamType,
        limit: 100,
      });
      setLeaderboardRows(response?.data || []);
      setTotalParticipants(response?.metadata?.totalParticipants || 0);
    } catch (error) {
      const message = error?.response?.data?.message || "Không thể tải bảng xếp hạng";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [selectedExamType]);

  const handleExamTypeChange = useCallback((event) => {
    setSelectedExamType(event.target.value || "");
  }, []);

  useEffect(() => {
    fetchExamTypes();
  }, [fetchExamTypes]);

  useEffect(() => {
    fetchLeaderboard();

    if (!selectedExamType) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      fetchLeaderboard();
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [fetchLeaderboard, selectedExamType]);

  const selectedExamTypeLabel = useMemo(() => {
    const selectedItem = examTypeOptions.find((item) => item.value === selectedExamType);
    return selectedItem?.label || selectedExamType;
  }, [examTypeOptions, selectedExamType]);

  return (
    <div className="min-h-screen bg-gray-50">
      <UserSidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />

      <main className="lg:ml-72 min-h-screen transition-all duration-300 flex flex-col">
        <div className="lg:hidden flex justify-between items-center p-4 bg-white border-b border-gray-200 sticky top-0 z-30">
          <h1 className="text-xl font-bold text-gray-800">Bảng xếp hạng</h1>
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="p-2 bg-gray-100 rounded-lg text-gray-600 hover:bg-gray-200"
          >
            <Menu size={24} />
          </button>
        </div>

        <div className="p-4 md:p-6 lg:p-8 space-y-6">
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
                  <Trophy className="w-7 h-7 text-red-600" />
                  Bảng xếp hạng theo loại đề
                </h2>
                <p className="text-gray-500 mt-1">
                  Chọn loại đề thi trước khi xem BXH
                </p>
              </div>

              <div className="flex items-center gap-3">
                <select
                  value={selectedExamType}
                  onChange={handleExamTypeChange}
                  disabled={loadingExamTypes}
                  className="px-4 py-2 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-semibold min-w-[170px]"
                >
                  <option value="">Chọn loại đề</option>
                  {examTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="px-4 py-2 rounded-xl bg-gray-50 border border-gray-200 text-gray-700 text-sm font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {totalParticipants} người tham gia
                </div>
                <button
                  onClick={fetchLeaderboard}
                  disabled={loading}
                  className="px-4 py-2 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                  Làm mới
                </button>
              </div>
            </div>
          </div>

          {!selectedExamType ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center text-gray-500">
              Vui lòng chọn loại đề thi để hiển thị bảng xếp hạng
            </div>
          ) : leaderboardRows.length === 0 && !loading ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center text-gray-500">
              Chưa có dữ liệu bảng xếp hạng cho loại đề {selectedExamTypeLabel}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                <div className="text-lg font-bold text-gray-900">{selectedExamTypeLabel}</div>
                <div className="text-sm text-gray-500">{totalParticipants} người</div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-gray-500 border-b border-gray-100">
                      <th className="px-6 py-3 w-20">Hạng</th>
                      <th className="px-6 py-3">Học viên</th>
                      <th className="px-6 py-3 text-center w-40">Điểm TB</th>
                      <th className="px-6 py-3 text-center w-40">Số lượt thi</th>
                      <th className="px-6 py-3 text-right w-56">Lần thi gần nhất</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboardRows.map((item) => (
                      <tr key={`${selectedExamType}-${item.userId}`} className="border-b border-gray-100 last:border-b-0">
                        <td className="px-6 py-4 font-bold text-red-600">#{item.rank}</td>
                        <td className="px-6 py-4">
                          <div className="font-semibold text-gray-900">{item.fullName}</div>
                          <div className="text-sm text-gray-500">{item.email || "-"}</div>
                        </td>
                        <td className="px-6 py-4 text-center font-bold text-gray-900">{item.averageScore}</td>
                        <td className="px-6 py-4 text-center text-gray-700">{item.totalAttempts}</td>
                        <td className="px-6 py-4 text-right text-gray-600">
                          {item.latestAttemptAt
                            ? new Date(item.latestAttemptAt).toLocaleString("vi-VN")
                            : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default LeaderboardPage;
